import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { BackButton } from '@helpcenter/back-button';
import { debounce } from 'lodash';
import { useIntl } from 'react-intl';
import { useRouterActions } from 'react-resource-router';
import type { UpdateSearchAction, ShowSearchAction } from 'state/actions/search';
import withAnalyticsEvents from '@atlaskit/analytics-next/withAnalyticsEvents';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import CrossIcon from '@atlaskit/icon/core/cross';
import SearchIcon from '@atlaskit/icon/core/search';
import { Box } from '@atlaskit/primitives/box';
import { Grid } from '@atlaskit/primitives/grid';
import { Inline } from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { media } from '@atlaskit/primitives/responsive/media';
import Textfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';
import { addParamsToUrl } from '@atlassian/help-center-common-util/history-qs';
import { SEARCH_BAR_HEIGHT, useQueryParams, SEARCH_QUERY_DEBOUNCE_TIME } from '../common';
import messages from './messages';
export interface Props {
	term: string;
	onChange: ((term: string) => UpdateSearchAction) | ((term: string) => void);
	onClose: () => ShowSearchAction;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
}
export const SearchBar = ({ term, onChange, createAnalyticsEvent }: Props) => {
	const { formatMessage } = useIntl();
	const [backButtonText, setBackButtonText] = useState<string>();
	const searchBarRef = useRef(null);
	const [searchQuery, setSearchQuery] = useState(term);
	useEffect(() => {
		let observer: ResizeObserver;
		const currentSearchBarRefRef = searchBarRef.current;
		if (window.ResizeObserver) {
			observer = new window.ResizeObserver((entries) => {
				for (const entry of entries) {
					setBackButtonText(entry.contentRect.width < 768 ? '' : undefined);
				}
			});
			if (currentSearchBarRefRef) {
				observer.observe(currentSearchBarRefRef);
			}
		}
		return () => {
			if (currentSearchBarRefRef) observer.unobserve(currentSearchBarRefRef);
		};
	}, []);
	const debouncedOnChangeMethod = useMemo(
		() =>
			debounce((value) => {
				onChange(value);
			}, SEARCH_QUERY_DEBOUNCE_TIME),
		[onChange],
	);
	const onSearchTermChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSearchQuery(e.target.value);
			if (FeatureGates.checkGate('debounce_change_in_portal_search')) {
				debouncedOnChangeMethod(e.target.value);
			} else {
				onChange(e.target.value);
			}
			if (createAnalyticsEvent) {
				createAnalyticsEvent({
					analyticsType: 'UI',
					action: 'changed',
					actionSubject: 'searchTermValue',
					actionSubjectId: 'searchTerm',
				}).fire();
			}
		},
		[createAnalyticsEvent, debouncedOnChangeMethod, onChange],
	);
	useEffect(() => {
		if (createAnalyticsEvent) {
			createAnalyticsEvent({
				analyticsType: 'UI',
				action: 'rendered',
				actionSubject: 'searchBarTextField',
				actionSubjectId: 'searchBar',
			}).fire();
		}
	}, [createAnalyticsEvent]);
	const { goBack, push } = useRouterActions();
	const onBackButtonClick = useCallback(() => {
		goBack() as unknown as void;
	}, [goBack]);
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const queryStringParams = useQueryParams();
	const onCrossIconClick = useCallback(() => {
		setSearchQuery('');
		onChange('');
		push(addParamsToUrl('', { ...queryStringParams, q: '' })) as unknown as void;
	}, [onChange, push, queryStringParams]);
	return (
		<Inline xcss={containerStyles} ref={searchBarRef}>
			<Grid gap="space.200" templateColumns="25fr 50fr 25fr" alignItems="center">
				<Box xcss={backButtonStyles}>
					<BackButton
						text={backButtonText}
						actionSubjectId="closeSearch"
						onClick={onBackButtonClick}
					/>
				</Box>
				<Box xcss={textFieldStyles}>
					<Textfield
						id="search-bar-input"
						value={searchQuery}
						name={formatMessage(messages.inputLabel)}
						aria-label={formatMessage(messages.inputPlaceholder)}
						onChange={onSearchTermChange}
						placeholder={formatMessage(messages.inputPlaceholder)}
						aria-describedby="dynamic-search-result"
						elemBeforeInput={
							<Box xcss={searchIconStyles}>
								<SearchIcon label="" spacing="spacious" color={token('color.icon', '#42526E')} />
							</Box>
						}
						elemAfterInput={
							<Box
								as="button"
								aria-label={formatMessage(messages.clearLabel)}
								xcss={crossIconStyles}
								onClick={onCrossIconClick}
							>
								<CrossIcon
									label={formatMessage(messages.clearLabel)}
									spacing="spacious"
									color={token('color.icon', '#42526E')}
								/>
							</Box>
						}
						// Disabling existing violations, should be fixed when revisited.

						autoFocus
					/>
				</Box>
				<Box />
			</Grid>
		</Inline>
	);
};
const containerStyles = xcss({
	display: 'flex',
	position: 'fixed',
	alignItems: 'center',
	width: '100vw',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	height: SEARCH_BAR_HEIGHT,
	backgroundColor: 'elevation.surface.overlay',
	borderBottomWidth: 'border.width',
	borderBottomStyle: 'solid',
	borderBottomColor: token('color.border', '#EBECF0'),
	zIndex: 'modal',
});
const backButtonStyles = xcss({
	[media.above.xxs]: {
		marginLeft: 'space.050',
	},
	[media.above.xs]: {
		marginLeft: 'space.100',
	},
	[media.above.sm]: {
		marginLeft: 'space.200',
	},
});
const textFieldStyles = xcss({
	height: 'space.500',
	marginLeft: 'space.100',
	marginRight: 'space.100',
	background: 'transparent',
	[media.above.xxs]: {
		width: '348px',
	},
	[media.above.xs]: {
		width: '432px',
	},
	[media.above.sm]: {
		width: '692px',
	},
	[media.above.md]: {
		width: '965px',
	},
});
const searchIconStyles = xcss({
	padding: 'space.100',
});
const crossIconStyles = xcss({
	background: 'transparent',
	cursor: 'pointer',
});
export default withAnalyticsEvents()(SearchBar);
